import type { AdsOnceki, AdsToplam } from "@/api/ads";
import type { K } from "@/api/ozet";
import { VerdictBox } from "@/components/VerdictBox";
import { fmtX, hukumToVerdictLevel } from "@/features/ozet/format";
import { DeltaBadge } from "@/features/ozet/GidisatHero";
import type { DeltaMode } from "@/hooks/useDeltaMode";
import { t } from "@/i18n";
import { fmtDate, fmtInt, fmtMoney } from "@/lib/utils";

interface HarcamaEtkisiProps {
  cur: AdsToplam | null | undefined;
  onceki: AdsOnceki | null | undefined;
  deltaMode: DeltaMode;
  /** Mevcut donemin gunluk harcama satiri sayisi (harcamasi > 0 olan gun
   * sayisi) — 0 ise donemde hic reklam verisi yok demektir (bkz. Kpi.tsx
   * `adsQuery.data?.gunluk`). Verilmezse 0 varsayilir. */
  gunlukSayisi?: number;
}

function pct(cur: number | null, prev: number | null): number | null {
  if (cur == null || prev == null) return null;
  if (Math.abs(prev) < 1e-9) return null;
  return ((cur - prev) / prev) * 100;
}

function fmtD(v: number | null): string {
  if (v == null) return "—";
  return (v > 0 ? "+" : "") + v.toFixed(1) + "%";
}

function k(cur: number | null, prev: number | null): K {
  return { cur, prev, delta_pct: cur != null && prev != null ? pct(cur, prev) : null };
}

interface Verdict {
  cls: "pozitif" | "negatif" | "karisik";
  icon: string;
  text: string;
}

/** Legacy `js/kpi.js` `renderSpendImpact()` portu — birebir ayni karar agaci
 * (madde E). Yalniz reklam platformunun KENDI atiflandirdigi metrikler
 * (spend/conversion_value/conversions/roas) kullanilir — magazanin GENEL
 * ciro/siparis sayisi DEGIL (legacy ile ayni kapsam). */
function hesaplaVerdict(cur: AdsToplam, onceki: AdsOnceki): Verdict {
  if (cur.conversion_value == null || onceki.conversion_value == null) return { cls: 'karisik', icon: 'ℹ', text: 'Dönüşüm değeri eksik veya kaynakların atıfları çakışıyor. Harcamanın satışa etkisi hesaplanamaz.' };
  const spendPct = pct(cur.spend, onceki.spend);
  const pvalPct = pct(cur.conversion_value, onceki.conversion_value);
  const roasTxt = `${fmtX(onceki.roas)} → ${fmtX(cur.roas)}`;

  if (spendPct == null) {
    return { cls: "karisik", icon: "⚖", text: t("kpi.harcama_etkisi.no_prev") };
  }

  const params = { spend: fmtD(spendPct), roas: roasTxt, pval: fmtD(pvalPct), pur: fmtD(pct(cur.conversions, onceki.conversions)) };

  if (spendPct >= 0) {
    if (cur.roas != null && onceki.roas != null && cur.roas >= onceki.roas) {
      return { cls: "pozitif", icon: "✅", text: t("kpi.harcama_etkisi.artti_pozitif", params) };
    }
    if (pvalPct != null && pvalPct > 0) {
      return { cls: "karisik", icon: "⚖", text: t("kpi.harcama_etkisi.artti_karisik", params) };
    }
    return { cls: "negatif", icon: "⚠", text: t("kpi.harcama_etkisi.artti_negatif", params) };
  }

  if (pvalPct != null && pvalPct >= 0) {
    return { cls: "pozitif", icon: "✅", text: t("kpi.harcama_etkisi.azaldi_pozitif", params) };
  }
  if (cur.roas != null && onceki.roas != null && cur.roas >= onceki.roas) {
    return { cls: "karisik", icon: "⚖", text: t("kpi.harcama_etkisi.azaldi_karisik", params) };
  }
  return { cls: "negatif", icon: "⚠", text: t("kpi.harcama_etkisi.azaldi_negatif", params) };
}

function Stat({ label, curTxt, badgeK, fmt, goodUp, deltaMode }: {
  label: string; curTxt: string; badgeK: K; fmt: (v: number | null | undefined) => string; goodUp?: boolean; deltaMode: DeltaMode;
}) {
  return (
    <div className="rounded-card border-[1.5px] border-line bg-bg/60 p-3">
      <div className="kpi-lbl">{label}</div>
      <div className="kpi-val mt-1.5 text-[22px]">{curTxt}</div>
      <DeltaBadge k={badgeK} fmt={fmt} goodUp={goodUp} mode={deltaMode} />
      <div className="text-[11px] text-muted">{t("kpi.harcama_etkisi.onceki_donem", { deger: fmt(badgeK.prev) })}</div>
    </div>
  );
}

/**
 * "Harcama Etkisi" kartı — Ortak Havuz bölümünün hemen altında (legacy
 * `#spendImpactCard`, madde E). Önceki dönem verisi yoksa (karsilastir=1
 * çağrılmadı ya da önceki pencerede hiç veri yoksa) HİÇ render edilmez —
 * legacy `card.classList.add('hidden')` ile aynı davranış.
 */
export function HarcamaEtkisi({ cur, onceki, deltaMode, gunlukSayisi = 0 }: HarcamaEtkisiProps) {
  if (!cur || !onceki) return null;
  if (onceki.spend === 0 && onceki.impressions === 0) return null;

  // Gorev madde 6: mevcut donemde HIC reklam verisi yoksa (harcama 0, tiklama
  // 0, gunluk satir 0) karar agaci (hesaplaVerdict) "-100% azaldi ve
  // geriledi" gibi yaniltici bir hukum uretiyordu — aslinda veri YOK, harcama
  // gercekten dusmedi. Bu durumda hukum yerine bilgi kutusu gosterilir.
  const veriYok = cur.spend === 0 && cur.clicks === 0 && gunlukSayisi === 0;
  if (veriYok) {
    return (
      <div className="card" data-testid="kpi-harcama-etkisi">
        <h3 className="mb-3 flex flex-wrap items-center gap-2 font-display text-[15px] font-bold text-ink">
          <span aria-hidden="true">💸</span>
          {t("kpi.harcama_etkisi.baslik")}
          <span className="font-sans text-xs font-normal text-muted">— {t("kpi.harcama_etkisi.alt")}</span>
        </h3>
        <div className="rounded-[9px] border-[1.5px] border-line bg-bg px-3 py-2 text-sm text-ink" data-testid="kpi-harcama-etkisi-veri-yok">
          {t("kpi.harcama_etkisi.veri_yok", { tarih: fmtDate(onceki.end) })}
          {onceki.spend > 0 && (
            <div className="mt-1 text-xs text-muted">
              {t("kpi.harcama_etkisi.onceki_donem", { deger: fmtMoney(onceki.spend) })}
            </div>
          )}
        </div>
      </div>
    );
  }

  const verdict = hesaplaVerdict(cur, onceki);

  return (
    <div className="card" data-testid="kpi-harcama-etkisi">
      <h3 className="mb-3 flex flex-wrap items-center gap-2 font-display text-[15px] font-bold text-ink">
        <span aria-hidden="true">💸</span>
        {t("kpi.harcama_etkisi.baslik")}
        <span className="font-sans text-xs font-normal text-muted">— {t("kpi.harcama_etkisi.alt")}</span>
      </h3>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat
          label={t("kpi.harcama_etkisi.harcama")}
          curTxt={fmtMoney(cur.spend)}
          badgeK={k(cur.spend, onceki.spend)}
          fmt={fmtMoney}
          goodUp={false}
          deltaMode={deltaMode}
        />
        <Stat
          label={t("kpi.harcama_etkisi.satis_degeri")}
          curTxt={fmtMoney(cur.conversion_value)}
          badgeK={k(cur.conversion_value, onceki.conversion_value)}
          fmt={fmtMoney}
          deltaMode={deltaMode}
        />
        <Stat
          label={t("kpi.harcama_etkisi.satin_alma")}
          curTxt={fmtInt(cur.conversions)}
          badgeK={k(cur.conversions, onceki.conversions)}
          fmt={fmtInt}
          deltaMode={deltaMode}
        />
        <Stat
          label={t("kpi.harcama_etkisi.roas")}
          curTxt={fmtX(cur.roas)}
          badgeK={k(cur.roas, onceki.roas)}
          fmt={fmtX}
          deltaMode={deltaMode}
        />
      </div>
      <div className="mt-3">
        <VerdictBox level={hukumToVerdictLevel(verdict.cls)} title={`${verdict.icon} ${verdict.text}`} />
      </div>
    </div>
  );
}
